import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { RecoilRoot } from "recoil";  // ✅ Recoil 추가
import MainLayout from "./layouts/MainLayout";
import Login from "./pages/Login";
import { getToken, deleteToken } from "./utils/auth";
import Orders from "./pages/Orders";
import OrderItem from './pages/OrderItem';

// Lazy loading for pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const UserDetail = lazy(() => import("./pages/UserDetail"));
const UserPointList = lazy(() => import("./pages/UserPointList"));
const UserHierarchy = lazy(() => import("./pages/UsersHierarchy"));
const ItemModify = lazy(() => import("./pages/ItemModify"));
const News = lazy(() => import("./pages/News"));
const Items = lazy(() => import("./pages/Items"));
const Partners = lazy(() => import("./pages/Partners"));
const PartnerCandidate = lazy(() => import("./pages/PartnerCandidate"));
const PartnerRsales = lazy(() => import("./pages/PartnerRsales"));
const Deposit = lazy(() => import("./pages/Deposit"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const SubCateManagement = lazy(() => import("./pages/SubCateManagement"));
const SubsOrderCancel = lazy(() => import("./pages/SubsOrderCancel"));
const Rentcar = lazy(() => import("./pages/Rentcar"));
const Rewards = lazy(() => import("./pages/Rewards"));
const BillingProcess = lazy(() => import("./pages/BillingProcess"));
const SalesSubs = lazy(() => import("./pages/SalesSubs"));
const SalesSubsDetails = lazy(() => import("./pages/SalesSubsDetails"));
const SalesValie = lazy(() => import("./pages/SalesValie"));
const SalesValieDetails = lazy(() => import("./pages/SalesValieDetails"));
const TransList = lazy(() => import("./pages/TransList"));
const SubscriptionPayments = lazy(() => import("./pages/SubscriptionPayments"));
const SubscriptionPaymentFailures = lazy(() => import("./pages/SubscriptionPaymentFailures"));
const SubscriptionSpeedCash = lazy(() => import("./pages/SubscriptionSpeedCash"));
const DirectorAllowance = lazy(() => import("./pages/DirectorAllowance"));
const BillingJobs = lazy(() => import("./pages/BillingJobs"));
const BillingJobDetails = lazy(() => import("./pages/BillingJobDetails"));

const pageRoutes = [
    { path: "/", component: Dashboard },
    { path: "/news", component: News },
    { path: "/user/detail", component: UserDetail },
    { path: "/user/point/list", component: UserPointList },
    { path: "/users/hierarchy", component: UserHierarchy },
    { path: "/item/modify", component: ItemModify },
    { path: "/items", component: Items },
    { path: "/partners", component: Partners },
    { path: "/partners/candidate", component: PartnerCandidate },
    { path: "/partners/rsales", component: PartnerRsales },
    { path: "/deposit", component: Deposit },
    { path: "/orders", component: Orders },
    { path: "/orderItem", component: OrderItem },
    { path: "/subscription/sub-cate", component: SubCateManagement },
    { path: "/subscriptions", component: Subscriptions },
    { path: "/subsordercancel", component: SubsOrderCancel },
    { path: "/rentcar", component: Rentcar },
    { path: "/rewards", component: Rewards },
    { path: "/billing", component: BillingProcess },
    { path: "/sales/subs", component: SalesSubs },
    { path: "/sales/subs/details", component: SalesSubsDetails },
    { path: "/sales/valie", component: SalesValie },
    { path: "/sales/valie/details", component: SalesValieDetails },
    { path: "/sales/transactions", component: TransList },
    { path: "/subscription/payments", component: SubscriptionPayments },
    { path: "/subscription/payment-failures", component: SubscriptionPaymentFailures },
    { path: "/subscription/speedcash", component: SubscriptionSpeedCash },
    { path: "/subscription/director-allowance", component: DirectorAllowance },
    { path: "/subscription/billing-jobs", component: BillingJobs },
    { path: "/subscription/billing-jobs/details", component: BillingJobDetails },
];

const App = () => {
    const [isSidebarOpen, setSidebarOpen] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(!!getToken());

    const handleSidebarToggle = () => {
        setSidebarOpen(!isSidebarOpen);
    };

    const handleLogin = () => {
        setIsLoggedIn(true);
    };

    const handleLogout = () => {
        deleteToken();
        setIsLoggedIn(false);
    };

    useEffect(() => {
        setIsLoggedIn(!!getToken());
    }, []);

    return (
        <RecoilRoot>  {/* ✅ RecoilRoot 추가 */}
            <Router>
                <Routes>
                    <Route path="/login" element={<Login onLogin={handleLogin} />} />
                    {pageRoutes.map(({ path, component: Component }) => (
                        <Route
                            key={path}
                            path={path}
                            element={
                                isLoggedIn ? (
                                    <MainLayout
                                        isSidebarOpen={isSidebarOpen}
                                        handleSidebarToggle={handleSidebarToggle}
                                        isLoggedIn={isLoggedIn}
                                        handleLogout={handleLogout}
                                    >
                                        <Suspense fallback={<div>Loading...</div>}>
                                            <Component />
                                        </Suspense>
                                    </MainLayout>
                                ) : (
                                    <Navigate to="/login" />
                                )
                            }
                        />
                    ))}
                </Routes>
            </Router>
        </RecoilRoot>
    );
};

export default App;